'use client'
import {
  Arrow as TooltipPrimitiveArrow,
  Content as TooltipPrimitiveContent,
  Portal as TooltipPrimitivePortal,
  Provider as TooltipPrimitiveProvider,
  Root as TooltipPrimitiveRoot,
  Trigger as TooltipPrimitiveTrigger,
} from '@radix-ui/react-tooltip'
import { canRenderInJsx } from '@repo/internal-tools-react-utils/src/lib/canRenderInJsx.ts'
import type {
  ComponentPropsWithoutRef,
  CustomComponentPropsWithRef,
  ReactNode,
} from 'react'
import { cn } from '../../utils/cn.ts'
import type { WithDataTestId } from '../componentProps.types.ts'
import { Content } from '../Content/Content.tsx'
import { ContentOptionsProvider } from '../Content/ContentOptionsProvider.tsx'
import { useContentOptionsContext } from '../Content/useContentOptionsContext.ts'

export type TooltipProviderProps = ComponentPropsWithoutRef<
  typeof TooltipPrimitiveProvider
>
/**
 * This is used for controlling options globally such as delayDuration and skipDelayDuration.
 */
export const TooltipProvider = TooltipPrimitiveProvider

export interface TooltipRootProps
  extends ComponentPropsWithoutRef<typeof TooltipPrimitiveRoot> {
  /**
   * Prevents the tooltip from showing.
   * This overrides `open` to always be false.
   */
  disabled?: boolean
}

export const TooltipRoot = (props: TooltipRootProps) => {
  const { open, disabled, ...restProps } = props

  return (
    <TooltipPrimitiveRoot
      // When disabled, forces open to be false to prevent tooltip
      // from showing. Otherwise, pass open as is.
      open={disabled ? false : open}
      {...restProps}
    />
  )
}

export type TooltipTriggerProps = CustomComponentPropsWithRef<
  typeof TooltipPrimitiveTrigger
>
export const TooltipTrigger = TooltipPrimitiveTrigger

export type TooltipPortalProps = CustomComponentPropsWithRef<
  typeof TooltipPrimitivePortal
>
export const TooltipPortal = TooltipPrimitivePortal

export type TooltipArrowProps = CustomComponentPropsWithRef<
  typeof TooltipPrimitiveArrow
>
export const TooltipArrow = (props: TooltipArrowProps) => {
  const { ref, className, ...restProps } = props

  return (
    <TooltipPrimitiveArrow
      ref={ref}
      className={cn(
        'fill-background-input_controls-selected-primary-selected',
        className,
      )}
      {...restProps}
    />
  )
}

export interface TooltipContentProps
  extends CustomComponentPropsWithRef<typeof TooltipPrimitiveContent>,
    WithDataTestId {
  shouldMatchTriggerWidth?: boolean
}

export const TooltipContent = (props: TooltipContentProps) => {
  const {
    ref,
    className,
    sideOffset = 4,
    shouldMatchTriggerWidth,
    ...restProps
  } = props

  return (
    <TooltipPrimitiveContent
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'bg-background-input_controls-selected-primary-selected text-foreground-inverse-primary animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 rounded-default py-space-y-sm px-space-x-contained z-50 max-h-(--radix-tooltip-content-available-height) overflow-auto shadow-md',
        shouldMatchTriggerWidth && 'w-(--radix-tooltip-trigger-width)',
        className,
      )}
      {...restProps}
    />
  )
}

export interface TooltipProps
  extends TooltipRootProps,
    Pick<
      TooltipContentProps,
      | 'shouldMatchTriggerWidth'
      | 'align'
      | 'alignOffset'
      | 'side'
      | 'sideOffset'
      | 'data-testid'
      | 'onClick'
    > {
  /**
   * Background color of the tooltip
   * - default: blue background according to the design
   * - primary: background-primary
   *
   * This is useful because it also changes the arrow color.
   */
  background?: 'default' | 'primary'
  label?: ReactNode
  description?: ReactNode
  contentClassName?: TooltipContentProps['className']
  /**
   * Overrides `label` and `description`
   */
  contentSlot?: ReactNode
  /**
   * To custom the arrow, you can pass your custom arrow component here.
   *
   * @example
   *
   * ```tsx
   * arrowSlot={<TooltipArrow className="fill-[red]" />}
   * ```
   */
  arrowSlot?: ReactNode
  /**
   * This optimizes rendering when the tooltip would always be disabled.
   *
   * ONLY USE THIS PROP WHEN YOU'RE SURE THE TOOLTIP WOULD ALWAYS BE DISABLED.
   *
   * Warning!: This could cause perf issue if the disabled state changes frequently.
   * Since React would have to mount multiple DOM nodes.
   */
  unmountOnDisabled?: boolean
}

export const Tooltip = (props: TooltipProps) => {
  const {
    children,
    label,
    description,
    contentClassName,
    contentSlot,
    arrowSlot,
    background,
    shouldMatchTriggerWidth,
    unmountOnDisabled,
    side,
    sideOffset,
    align,
    alignOffset,
    onClick,
    'data-testid': dataTestId,
    ...restProps
  } = props

  const contentOptions = useContentOptionsContext()

  // This skips all unnecessary rendering of Tooltip
  // and render the children directly when the tooltip is always disabled.
  if (unmountOnDisabled && restProps.disabled) return children

  const { size: contentSize = 'xs', ...restContentOptions } = contentOptions

  return (
    <TooltipRoot {...restProps}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        shouldMatchTriggerWidth={shouldMatchTriggerWidth}
        className={cn(
          background === 'primary' &&
            'bg-background-primary text-foreground-primary',
          contentClassName,
        )}
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        onClick={onClick}
        data-testid={dataTestId}
      >
        {canRenderInJsx(contentSlot) ? (
          <ContentOptionsProvider size={contentSize} {...restContentOptions}>
            {contentSlot}
          </ContentOptionsProvider>
        ) : (
          <Content
            // Prevent direction from being influenced by the upper context.
            // According to the design, this content should always be in vertical direction.
            direction="vertical"
            size={contentSize}
            label={label}
            description={description}
          />
        )}
        {arrowSlot ?? (
          <TooltipArrow
            className={
              background === 'primary' ? 'fill-background-primary' : undefined
            }
          />
        )}
      </TooltipContent>
    </TooltipRoot>
  )
}
